/* eslint-disable max-len */
/* eslint-disable no-param-reassign */
import React, { useState, useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Alert, MenuItem, TextField, Button, Snackbar, Dialog, DialogTitle, Box, IconButton, InputLabel,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-pro';
import CloseIcon from '@mui/icons-material/Close';

import {
  getRowIdxForId,
  getLocalStorageTablePref,
  updateTablePreferenceLocalStorage,
  diffListOfObjects,
  getCommonAttributes,
} from '../utils/index';
import { getAndFormat30DaysAgo } from '../utils/formatters';
import ClientTextField from '../components/clientFields/ClientTextField';
import ClientDropDown from '../components/clientFields/ClientDropDown';
import MPAutocomplete from '../components/clientFields/MPAutocomplete';
import logger from '../utils/logger';
import APIWrapper from '../utils/graphqlwrapper';
import {
  codeColumns, channelCategoryEnum, valueFormatters, smsChannelsEnum, revenueTypesEnum,
} from '../utils/schemaInfo';
import { CustomDatePicker } from '../components/DateAndTimePickers';
import AppContext from '../context';
import { createCodeRows } from '../utils/tableHelpers';
import AdvancedSearchV2 from '../components/AdvancedSearchV2';
import AdvancedSearchChip from '../components/AdvancedSearchChip';
import ServerTable from '../components/ServerTable';
import Loading from '../components/Loading';
import { VITE_ENVIRON } from '../aws-config';

const structure = {
  type: [
    'Select one',
    ...revenueTypesEnum,
  ],
  channel: [ // used to populate sms channel options
    'Select one',
    ...smsChannelsEnum,
  ],
  channel_category: [
    'Select one',
    ...channelCategoryEnum,
  ],
};

function CodeGroupings(props) {
  const { client: clientProp } = props;
  const { client: clientParam } = useParams();
  const client = clientProp || clientParam;
  const initColumnVisibilityModel = { from_goop: false, createdAt: false };
  document.title = 'MissionPortal | Codes';

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(initColumnVisibilityModel);
  const [snackMessage, setSnackMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackWithConfirm, setSnackWithConfirm] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [newSource, setNewSource] = useState(false);
  // tracks data in create/edit modal
  const [editRowData, setEditRowData] = useState({});
  const [bulkEditFormData, setBulkEditFormData] = useState({});
  const [alertMessage, setAlertMessage] = useState();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [errorRows, setErrorRows] = useState([]);
  const gridApiRef = useGridApiRef();
  const editingRowOriginalRef = React.useRef(null);

  // codes modified for preview/bulk edit and to send to batchEditDynamoData mutation
  const [codesToUpdate, setCodesToUpdate] = useState([]);
  const [previewCodesAll, setPreviewCodesAll] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const { authenticated, setShouldLogOut } = useContext(AppContext);
  const [sourceSearch, setSourceSearch] = useState('');
  const [sourceOptions, setSourceOptions] = useState([]);
  const [sourceData, setSourceData] = useState([]);
  const [searching, setSearching] = useState(false);
  const [pageState, setPageState] = useState({
    page: 0, // mui pages are 0 indexed
    pageSize: 50,
    data: [],
    total: 0,
    isPreview: false,
    previewData: [],
  });

  // default/locked criteria the user can't delete - search must be scoped to current client
  const lockedCriteria = [
    {
      col: 'client_id',
      op: 'eq',
      criteria: client,
    },
  ];

  const thirtyDaysAgoString = getAndFormat30DaysAgo();

  // niceley formatted query for our API
  const [advancedSearchQuery, setAdvancedSearchQuery] = useState({
    client_id: {
      eq: client,
    },
    and: {
      most_recent_transaction_date: {
        gte: thirtyDaysAgoString,
      },
      and: {
        source: {
          exists: 'false',
        },
      },
    },
  });

  // when we have user input from the MPAutocomplete component - we want to run a query
  const searchForSources = async (subString) => {
    /**
     * searches Sources table for a direct or partial match to the substring passed in
     *
     * @param {String} subString the search that the user has typed into the field
     * @returns {Array} an array of sources from the sources table that match the criteria
     */
    const filter = {
      client_id: { eq: client },
    };
    filter.and = {
      or: [
        { source: { eq: `${subString}` } }, // search for full string
        { source: { matchPhrasePrefix: `${subString}` } }, // search for the phrase
      ],
    };
    setSearching(true);
    const results = await APIWrapper.queryApi(
      { query: 'searchSources' },
      setShouldLogOut,
      {
        filter,
        limit: 500,
      },
    ).catch((err) => { logger.error(err); });
    setSearching(false);
    return results.data[0];
  };

  // onclick handler for edit icon in the table actions column
  const handleEditClick = (event, row) => {
    const curRow = row.row;
    if (!curRow.from_goop) {
      setEditRowData(curRow);
      setIsDialogOpen(true);
    } else {
      setSnackMessage('The source was created in Goop and so you need to update it in Goop.');
      setOpenSnackbar(true);
    }
  };

  // also clearing the form here in case the user exited the form by clicking off the form
  // in which case the values may still be populated bc form state was not cleared
  const handleBulkEditClick = () => {
    // clear existing values from form state
    const isPreview = false;
    const previewData = [];
    const formData = getCommonAttributes(selectedRows);
    setPageState({ ...pageState, isPreview, previewData });
    setBulkEditFormData(formData);
    setCodesToUpdate([]);

    setBulkEditOpen(true);
  };

  const getPreviewData = (curCodesData, selectedRows_, bulkEditFormData_) => {
    const selectedRowIds = selectedRows_.map((row) => row.id);
    const previewData = [];
    const codesToEdit = [];
    // Only apply fields with non-empty values so we don't overwrite existing code data with blanks
    const nonEmptyBulkEdits = Object.fromEntries(
      Object.entries(bulkEditFormData_).filter(
        ([, v]) => v !== null && v !== undefined && !(typeof v === 'string' && v.trim() === ''),
      ),
    );
    curCodesData.forEach((obj) => {
      if (selectedRowIds.indexOf(obj.id) > -1) {
        const updatedObj = { ...obj, ...nonEmptyBulkEdits };
        const codePatch = { id: obj.id, code: obj.code, ...nonEmptyBulkEdits };
        previewData.push(updatedObj);
        codesToEdit.push(codePatch);
      } else {
        previewData.push(obj);
      }
    });
    setCodesToUpdate(codesToEdit);
    setPreviewCodesAll(previewData);
    return previewData;
  };

  const validateInput = (row, requiredFields) => {
    /**
     * takes a row and checks whether the required fields have values
     * @param {Object} row the row being edited or the bulk edit data being previewed
     * @param {Array} requiredFields the fields that are required for this row to be valid
     * @returns {Array} an array of missing fields, empty if all required fields have values
     */
    const missingFields = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const field of requiredFields) {
      if (row[field] === null || row[field] === undefined || row[field] === '') {
        missingFields.push(field);
      }
    }
    return missingFields;
  };

  // made preview into a state for the base table (instead of creating a separate preview component)
  const togglePreview = () => {
    const isPreview = true;
    const previewData = getPreviewData(pageState.data, selectedRows, bulkEditFormData);
    setPageState({ ...pageState, isPreview, previewData });
    setBulkEditOpen(false);
    setSnackMessage('Do you wish to save the previewed changes?');
    setSnackWithConfirm(true);
    setOpenSnackbar(true);
  };

  const isSmsChannelCategory = (category) => category === 'SMS_Longcode' || category === 'SMS_Shortcode';

  // clears an existing channel value when it doesn't belong to the SMS channel list, so a code
  // can't be left showing a non-SMS channel (e.g. leftover free text) under an SMS channel category
  const getChannelResetForCategory = (row, category) => (
    isSmsChannelCategory(category) && row.channel && !smsChannelsEnum.includes(row.channel)
      ? { channel: '' }
      : {}
  );

  const setter = (key, value, extra = null) => {
    /**
     * responds to onChange event in order to track updates to the row from the
     * create/edit modal/dialog; updates state in this component which will be
     * used to send to the graphql mutation
     * @param {String} key the name of the field being updated (e.g. source, audience, etc.)
     * @param {String} value the new value for the field being updated
     * @param {String} extra used for when we want to set an additional field based on the value
     * selected in the dropdown (e.g. setting channel category based on source selection)
     */
    if (key === 'source' && extra) {
      // if we are setting the source, we also want to set the channel category
      // if the source is SMS_Longcode, we also want to set the send_date on the codes
      // level, pulled from the matching source record
      const matchedSource = sourceData.find((item) => item.source === value);
      setEditRowData(
        // eslint-disable-next-line no-shadow
        (editRowData) => {
          const channelReset = getChannelResetForCategory(editRowData, extra);
          if (extra === 'SMS_Longcode') {
            return {
              ...editRowData,
              channel_category: extra,
              [key]: value,
              type: 'p2p',
              send_date: matchedSource ? matchedSource.send_date : '',
              ...channelReset,
            };
          }
          // reset send_date if the source is not SMS_Longcode
          return {
            ...editRowData, channel_category: extra, [key]: value, send_date: null, ...channelReset,
          };
        },
      );
    } else if (key === 'channel_category') {
      setEditRowData(
        // eslint-disable-next-line no-shadow
        (editRowData) => ({
          ...editRowData,
          [key]: value,
          ...getChannelResetForCategory(editRowData, value),
        }),
      );
    } else {
      // eslint-disable-next-line no-shadow
      setEditRowData((editRowData) => ({ ...editRowData, [key]: value }));
    }
  };

  const bulkEditsetter = (key, value, extra = null) => {
    /**
     * responds to onChange event in order to track updates to the row from the
     * create/edit modal/dialog; updates state in this component which will be
     * used to send to the graphql mutation
     * @param {String} key the name of the field being updated (e.g. source, audience, etc.)
     * @param {String} value the new value for the field being updated
     * @param {String} extra used for when we want to set an additional field based on the value
     * selected in the dropdown (e.g. setting channel category based on source selection)
     */
    if (key === 'source' && extra) {
      // if we are setting the source, we also want to set the channel category
      // if the source is SMS_Longcode, we also want to set the send_date on the codes
      // level, pulled from the matching source record
      const matchedSource = sourceData.find((item) => item.source === value);
      setBulkEditFormData(
        // eslint-disable-next-line no-shadow
        (bulkEditFormData) => {
          const channelReset = getChannelResetForCategory(bulkEditFormData, extra);
          if (extra === 'SMS_Longcode') {
            return {
              ...bulkEditFormData,
              channel_category: extra,
              [key]: value,
              type: 'p2p',
              send_date: matchedSource ? matchedSource.send_date : '',
              ...channelReset,
            };
          }
          return {
            ...bulkEditFormData, channel_category: extra, [key]: value, ...channelReset,
          };
        },
      );
    } else if (key === 'channel_category') {
      setBulkEditFormData(
        // eslint-disable-next-line no-shadow
        (bulkEditFormData) => ({
          ...bulkEditFormData,
          [key]: value,
          ...getChannelResetForCategory(bulkEditFormData, value),
        }),
      );
    } else {
      // eslint-disable-next-line no-shadow
      setBulkEditFormData((bulkEditFormData) => ({ ...bulkEditFormData, [key]: value }));
    }
  };

  // clear state for bulk edit form
  const handleBulkEditFormCancel = () => {
    const isPreview = false;
    const previewData = [];
    setPageState({ ...pageState, isPreview, previewData });
    setBulkEditFormData({});
    setSelectedRows([]);
    setCodesToUpdate([]);
    setBulkEditOpen(false);
  };

  const actionColumns = [
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 75,
      disableClickEventBubbling: true,
      cellClassName: 'actions',
      disableReorder: true,
      excludeFromSearch: true,
      getActions: (row) => [
        <GridActionsCellItem
          key="edit"
          icon={<EditIcon color={row.row.from_goop ? 'disabled' : 'inherit'} />}
          label="Edit"
          className="textPrimary"
          onClick={(ev) => handleEditClick(ev, row)}
          color="inherit"
          size="medium"
        />,
      ],
    },
  ];

  const channelColumn = [{
    field: 'channel',
    headerName: 'Channel',
    width: 200,
    format: 'String',
    sortable: false,
    editable: true,
    valueSetter: (value, row) => ({ ...row, channel: value }),
    renderEditCell: (params) => {
      const isSmsType = params.row.channel_category === 'SMS_Longcode'
        || params.row.channel_category === 'SMS_Shortcode';
      const handleChange = (value) => {
        gridApiRef.current.setEditCellValue({ id: params.id, field: 'channel', value });
      };
      if (isSmsType) {
        return (
          <TextField
            select
            sx={{ width: '100%' }}
            value={params.value || ''}
            onChange={(e) => handleChange(e.target.value)}
            error={false}
          >
            {structure.channel.filter((opt) => opt !== 'Select one').map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </TextField>
        );
      }
      return (
        <TextField
          value={params.value || ''}
          onChange={(e) => handleChange(e.target.value)}
        />
      );
    },
    preProcessEditCellProps: (params) => ({ ...params.props, error: false }),
  }];

  // SMS_Longcode sources are structured as "Mailing Name | Send Date" (mirrors Sources.jsx);
  // these helpers keep the inline "source" cell in sync while a new SMS_Longcode source is
  // being created, no matter which of the source/channel category/send date cells changes last.
  const getMailingNameFromSmsLongcodeSource = (sourceValue) => {
    if (!sourceValue) return sourceValue;
    const idx = sourceValue.lastIndexOf(' | ');
    return idx > -1 ? sourceValue.substring(0, idx) : sourceValue;
  };

  const applySmsLongcodeSource = (rowId, mailingName, sendDate) => {
    const trimmedMailingName = mailingName ? mailingName.trim() : mailingName;
    const value = trimmedMailingName && sendDate ? `${trimmedMailingName} | ${sendDate}` : trimmedMailingName;
    gridApiRef.current.setEditCellValue({ id: rowId, field: 'source', value });
    gridApiRef.current.updateRows([{ id: rowId, source: value }]);
    setEditRowData((prev) => (prev['code-source-row-id'] === rowId ? { ...prev, 'code-source': value } : prev));
  };

  const channelCategoryColumn = [{
    field: 'channel_category',
    headerName: 'Channel Category',
    type: 'singleSelect',
    sortable: false,
    editable: true,
    format: 'options',
    options: channelCategoryEnum,
    renderEditCell: (params) => {
      const sourceIsNew = params.row.source
        && !sourceData.some((item) => item.source === params.row.source);
        // if we're not creating a new source
        // and if the row already has a source assigned
        // render non-editable static text
      if ((!newSource && !sourceIsNew) && params.row.source) {
        return (
          <Box
            sx={{
              px: 2, display: 'flex', alignItems: 'center', width: '100%',
            }}
          >
            {params.value || ''}
          </Box>
        );
      }
      return (
        <TextField
          select
          value={params.value || ''}
          onChange={(e) => {
            const { value } = e.target;
            gridApiRef.current.setEditCellValue({ id: params.id, field: 'channel_category', value });
            if (newSource && value === 'SMS_Longcode') {
              const mailingName = getMailingNameFromSmsLongcodeSource(params.row.source);
              applySmsLongcodeSource(params.id, mailingName, params.row.send_date);
            }
          }}
          sx={{ width: '100%' }}
        >
          {channelCategoryEnum.map((opt) => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </TextField>
      );
    },
  }];

  const sendDateColumn = [{
    field: 'send_date',
    headerName: 'Send Date',
    sortable: false,
    editable: true,
    format: 'AWSDate',
    width: 200,
    valueFormatter: valueFormatters.dateFormatterAWSDate,
    formatterName: 'dateFormatterAWSDate',
    renderEditCell: (params) => {
      const handleDateChange = (field, value) => {
        gridApiRef.current.setEditCellValue({ id: params.id, field, value });
        if (newSource && params.row.channel_category === 'SMS_Longcode') {
          const mailingName = getMailingNameFromSmsLongcodeSource(params.row.source);
          applySmsLongcodeSource(params.id, mailingName, value);
        }
      };
      // send_date can only be edited while the code isn't grouped to an existing source
      if (params.row.source && sourceData.some((item) => item.source === params.row.source)) {
        return (
          <Box
            sx={{
              px: 2, display: 'flex', alignItems: 'center', width: '100%',
            }}
          >
            {params.value || ''}
          </Box>
        );
      }
      return (
        <CustomDatePicker
          format="MM/DD/YYYY"
          setCriteria={handleDateChange}
          criteria={params.value || ''}
          name="send_date"
        />
      );
    },
  }];

  const sourceColumn = [
    {
      field: 'source',
      headerName: 'Source',
      width: 200,
      editable: true,
      format: 'String',
      valueSetter: (value, row) => ({ ...row, source: value }),
      renderEditCell: (params) => {
        const handleSourceSelect = (field, value, creatingNewSource) => {
          // if creating a new source for an SMS_Longcode row, combine it with the send_date
          // these are formatted as "MailingName | send_date" in the source creation UI, so carrying over here
          const shouldCombineWithSendDate = creatingNewSource
            && params.row.channel_category === 'SMS_Longcode'
            && params.row.send_date;
          const finalValue = shouldCombineWithSendDate
            ? `${value.trim()} | ${params.row.send_date}`
            : value;
          setEditRowData((prevRowData) => ({ ...prevRowData, [field]: finalValue, 'code-source-row-id': params.row.id }));
          gridApiRef.current.setEditCellValue({
            id: params.row.id,
            field: 'source',
            value: finalValue,
          });
          gridApiRef.current.updateRows([{ id: params.row.id, source: finalValue }]);
          if (!creatingNewSource) {
            const matched = sourceData.find((item) => item.source === value);
            if (matched) {
              gridApiRef.current.updateRows([{
                id: params.row.id,
                channel_category: matched.channel_category,
                send_date: matched.send_date,
              }]);
              gridApiRef.current.setEditCellValue({
                id: params.row.id,
                field: 'channel_category',
                value: matched.channel_category,
              });
              gridApiRef.current.setEditCellValue({
                id: params.row.id,
                field: 'send_date',
                value: matched.send_date,
              });
            }
          }
        };
        return (
          <MPAutocomplete
            key={params.id}
            setFunction={handleSourceSelect}
            dropdownOptions={sourceOptions}
            selectedValue={params.value || ''}
            editRowData={editRowData}
            setSourceSearch={setSourceSearch}
            field="code-source"
            setNewSource={setNewSource}
          />
        );
      },
    },
  ];

  const columns = actionColumns.concat(
    codeColumns.slice(0, 2),
    sourceColumn,
    channelColumn,
    channelCategoryColumn,
    sendDateColumn,
    codeColumns.slice(2),
  );

  // part of component state; I would rather have it above but columns has
  // dependencies on methods defined above
  const [orderedColumns, setOrderedColumns] = useState(columns);

  const getSourcesForDropdown = async () => {
    if (authenticated && client) {
      try {
        const results = await searchForSources(sourceSearch);
        const sources = [];
        const sourcesAdditionalData = [];
        results.forEach((item) => {
          sources.push(`${item.source} - ${item.channel_category}`);
          sourcesAdditionalData.push({
            source: item.source,
            channel_category: item.channel_category,
            send_date: item.send_date,
          });
        });
        setSourceOptions(sources);
        setSourceData(sourcesAdditionalData);
      } catch (err) {
        logger.error(err);
      }
    }
  };

  const getErrorMessage = (errorLike, fallbackMessage) => {
    if (!errorLike) {
      return fallbackMessage;
    }

    const errs = Array.isArray(errorLike)
      ? errorLike
      : errorLike.errors;

    if (Array.isArray(errs) && errs.length > 0) {
      const messages = errs
        .map((item) => (item && item.message ? item.message : null))
        .filter(Boolean);
      if (messages.length > 0) {
        return messages.join(' ');
      }
    }

    if (errorLike.message) {
      return errorLike.message;
    }

    return fallbackMessage;
  };

  const saveFunc = (newRow) => {
    if (authenticated) {
      // default the revenue type to p2p for all SMS_Longcode codes
      const copyNewRow = { ...newRow };
      if (newRow.channel_category === 'SMS_Longcode') {
        copyNewRow.type = 'p2p';
        copyNewRow.send_date = newRow.send_date;
      }
      copyNewRow.fixed_cost_cpa_override = newRow.fixed_cost_cpa_override
        ? parseFloat(newRow.fixed_cost_cpa_override) : null;
      copyNewRow.fixed_cost_media_spend = newRow.fixed_cost_media_spend ? parseFloat(
        newRow.fixed_cost_media_spend,
      ) : null;
      copyNewRow.most_recent_spend = newRow.most_recent_spend
        ? parseFloat(newRow.most_recent_spend) : null;

      // removing fields that aren't specified in input for the query/mutation
      const keysToDelete = [
        'client_id', 'code', 'most_recent_spend_date', 'most_recent_transaction_date', 'platforms_with_spend',
        'revenue', 'transactions', 'total_spend', 'updatedAt', 'createdAt', 'page', 'actblue_managing_entities',
      ];
      keysToDelete.forEach((key) => {
        delete copyNewRow[key];
      });
      APIWrapper.queryApi({ mutation: 'updateCodes' }, setShouldLogOut, {
        input: copyNewRow,
      })
        .then((resp) => {
          const fallbackMessage = 'Changes to the row could not be saved.';
          if (resp.errors && resp.errors.length) {
            setSnackMessage(getErrorMessage(resp.errors, fallbackMessage));
            setOpenSnackbar(true);
            setAdvancedSearchQuery((q) => ({ ...q }));
            return;
          }
          const results = (resp.data && resp.data[0] && resp.data[0][0]) ? resp.data[0][0] : null;
          if (results) {
            setSnackMessage(`${results.code} was updated.`);
            setOpenSnackbar(true);
          } else {
            setSnackMessage(fallbackMessage);
            setOpenSnackbar(true);
          }
        })
        .catch((err) => {
          logger.error(err);
          const errMsg = getErrorMessage(err, 'Changes to the row could not be saved.');
          setSnackMessage(errMsg);
          setOpenSnackbar(true);
          setAdvancedSearchQuery((q) => ({ ...q }));
        });
    }
  };

  // used for Snackbar (handles click Cancel and Close - X)
  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    // if in preview state
    // (user clicks X to cancel the update/preview)
    if (pageState.isPreview) {
      setSelectedRows([]);
      setPageState({ ...pageState, isPreview: false, previewData: [] });
      setCodesToUpdate([]);
      setBulkEditFormData({});
    }

    setOpenSnackbar(false);
    setSnackMessage('');
    setSnackWithConfirm(false);
  };

  // for dialog
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleBulkEditClose = () => {
    setBulkEditOpen(false);
  };

  const handleModalSave = () => {
    /**
     * validates row being edited and triggers save if it is good
     */
    let missingFields = [];
    if (editRowData.channel_category === 'SMS_Longcode') {
      if (editRowData.channel === 'MissionSMS' && editRowData.source) {
        missingFields = validateInput(editRowData, ['source', 'version', 'channel', 'jira_ticket_num', 'send_date']);
      } else if (editRowData.source) {
        missingFields = validateInput(editRowData, ['source', 'version', 'channel', 'send_date']);
      }
    }
    if (missingFields.length > 0) {
      setSnackMessage(`You are missing some required fields, please make selections for the following fields: ${missingFields.join(', ')}`);
      setOpenSnackbar(true);
    } else {
      // update the row in the table manually
      const rowIdx = getRowIdxForId(editRowData.id, pageState.data);
      if (rowIdx > -1) {
        const copyRows = pageState.data.slice(0);
        copyRows.splice(rowIdx, 1, editRowData);

        setPageState((old) => ({ ...old, data: copyRows }));
        saveFunc(editRowData);
        setIsDialogOpen(false);
      }
    }
  };

  // removing fields that aren't specified in input for the query/mutation
  const getCodeWithoutUnmutableFields = (code) => {
    const copyCode = { ...code };
    const keysToDelete = ['createdAt', 'client_id', 'code', 'fixed_cost_cpa_override', 'fixed_cost_media_spend', 'from_goop',
      'most_recent_spend', 'most_recent_spend_date', 'most_recent_transaction_date', 'platforms_with_spend', 'revenue', 'transactions', 'total_spend', 'page', 'actblue_managing_entities'];
    keysToDelete.forEach((key) => { delete copyCode[key]; });
    return copyCode;
  };

  const getPreviewSavedSummary = (status) => {
    /**
     * returns a list of rejected or fulfilled codes depending on the status parameter passed in.
     * @param {String} status rejected or fulfilled status from the lambda response
     * @returns {String} a comma separated list of codes
     */
    const msg = codesToUpdate.reduce((accum, cur) => {
      if (status === 'rejected') {
        if (cur.source === null) {
          return `${accum} ${cur.code},`;
        }
      } else if (status === 'fulfilled') {
        if (cur.source !== null) {
          return `${accum} ${cur.code},`;
        }
      }
      return `${accum}`;
    }, '');
    return msg;
  };

  const bulkUpdateFunc = () => {
    /**
     * Triggered when user confirms the previewed changes in the snackbar message. Takes the
     * list of codes to update, and sends them to the batchEditDynamoData lambda function for processing
     */
    if (authenticated) {
      // default revenue type to p2p if channel_category is SMS_Longcode
      const normalizedCodesToUpdate = codesToUpdate.map((code) => (
        code.channel_category === 'SMS_Longcode'
          ? { ...code, type: 'p2p' }
          : code
      ));

      const cleanedCodesToUpdate = normalizedCodesToUpdate.map((code) => getCodeWithoutUnmutableFields(code));
      const requests = cleanedCodesToUpdate.map((item) => ({ Item: item }));
      const inputObj = { requests, tableName: VITE_ENVIRON === 'production' ? 'Codes' : `Codes-missionportal-${VITE_ENVIRON}` };
      const input = JSON.stringify(inputObj);

      APIWrapper.queryApi(
        { query: 'batchEditDynamoData' },
        setShouldLogOut,
        { input },
      )
        .then((resp) => {
          // handle actual appsync errors
          if (resp.errors && resp.errors.length) {
            setSnackMessage('The previewed changes could not be saved. Please submit a tools help ticket so we can investigate the issue.');
            setOpenSnackbar(true);
            setPageState({
              ...pageState, isPreview: false, previewData: [],
            });
            setCodesToUpdate([]);
            setBulkEditFormData({});
            setAdvancedSearchQuery((q) => ({ ...q }));
          } else {
            const { successCount, errors } = JSON.parse(resp.data[0]);
            let message = '';
            if (errors.length > 0) {
              const uniqueErrors = [...new Set(
                errors
                  .flatMap((e) => String(e.reason || '').split('; '))
                  .map((r) => r.replace(/ for id .+$/, ''))
                  .filter(Boolean),
              )];
              message += `${errors.length} code(s) failed to update: ${uniqueErrors.join('; ')}.`;
            }
            if (successCount > 0) {
              const fulList = getPreviewSavedSummary('fulfilled').slice(0, -1);
              message += ` Successfully updated the following codes:${fulList}.`;
            }
            setSnackMessage(message);
            setOpenSnackbar(true);
            setPageState({
              ...pageState, data: errors.length > 0 ? [] : previewCodesAll, isPreview: false, previewData: [],
            });
            setCodesToUpdate([]);
            setBulkEditFormData({});
            if (errors.length > 0) {
              setAdvancedSearchQuery((q) => ({ ...q }));
            }
          }
        });
    }
  };

  const handleSnackBulkEditUpdateConfirm = () => {
    bulkUpdateFunc();
    setSnackWithConfirm(false);
    setOpenSnackbar(false);
    setSnackMessage('');
  };

  const handleSnackBulkEditUpdateBack = () => {
    if (pageState.isPreview) {
      setPageState({ ...pageState, isPreview: false });
      setBulkEditOpen(true);
    }
    setSnackWithConfirm(false);
    setOpenSnackbar(false);
    setSnackMessage('');
  };

  const actionForBulkEditUpdate = (
    <>
      <Button color="secondary" size="small" onClick={handleSnackBulkEditUpdateConfirm}>
        Save
      </Button>
      <Button color="secondary" size="small" onClick={handleSnackBulkEditUpdateBack}>
        Back
      </Button>
      <Button color="secondary" size="small" onClick={handleClose}>
        Cancel
      </Button>
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={handleClose}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  );
  const updateCode = async (data) => {
    try {
      const resp = await APIWrapper.queryApi(
        { mutation: 'updateCodes' },
        setShouldLogOut,
        { input: data },
      );
      if (resp.errors) {
        const errMsg = resp.errors[0].message || 'The code could not be updated.';
        setAlertMessage({ severity: 'error', children: errMsg });
        setSnackbarOpen(true);
        throw new Error(errMsg);
      }
      setAlertMessage({ severity: 'success', children: 'Successfully updated row' });
      setSnackbarOpen(true);
      return resp.data[0][0] || null;
    } catch (err) {
      if (err.errors) {
        logger.error(err);
        const errMsg = err.errors[0].message || 'Changes to the row could not be saved.';
        setAlertMessage({ severity: 'error', children: errMsg });
        setSnackbarOpen(true);
      }
      throw err;
    }
  };

  const checkSourceBeforeCreating = async (data) => {
    const filter = { client_id: { eq: client } };

    const source = APIWrapper.queryApi(
      { query: 'sourceBySource' },
      setShouldLogOut,
      { source: data, filter },
    )
      .then((resp) => resp.data);
    return source;
  };

  const createSource = async (data) => {
    let createdSuccessfully;
    await APIWrapper.queryApi(
      { mutation: 'createSources' },
      setShouldLogOut,
      { input: data },
    )
      .then((resp) => {
        const results = resp.data[0][0] || null;
        if (resp.errors) {
          // check for errors first
          const errMsg = resp.errors[0].message || 'The source could not be created.';
          setAlertMessage({
            severity: 'error',
            children: errMsg,
          });
          setSnackbarOpen(true);
        } else if (results) {
          setSourceOptions((prevState) => [...prevState, `${data.source} - ${data.channel_category}`]);
          setSourceData((prevState) => [...prevState, { source: data.source, channel_category: data.channel_category, send_date: data.send_date }]);
          createdSuccessfully = true;
        }
      }).catch((err) => {
        logger.error(err);
        const errMsg = err.errors[0].message || 'The source could not be created.';
        setAlertMessage({
          severity: 'error',
          children: errMsg,
        });
        setSnackbarOpen(true);
        createdSuccessfully = false;
      });
    return createdSuccessfully;
  };

  const handleInlineEditError = (err) => {
    setAlertMessage({
      severity: 'error',
      children: err.message,
    });
  };

  const handleInlineEdit = async (newRow, oldRow) => {
    setAlertMessage({});

    // If nothing changed and no source was selected via the autocomplete, skip validation entirely
    const hasChanges = Object.keys(newRow).some((key) => newRow[key] !== oldRow[key]);
    if (!hasChanges) {
      return oldRow;
    }

    const inlineSourceSelected = editRowData['code-source-row-id'] === newRow.id
      ? editRowData['code-source']
      : null;
    const matchedSourceData = inlineSourceSelected
      ? sourceData.find((item) => item.source === inlineSourceSelected)
      : null;
    // get channel_category and send_date for source if it was selected inline
    const effectiveRow = {
      ...newRow,
      ...(inlineSourceSelected ? {
        source: inlineSourceSelected,
        ...(matchedSourceData ? { channel_category: matchedSourceData.channel_category, send_date: matchedSourceData.send_date } : {}),
      } : {}),
    };

    // Guard against saving a source name that was typed but never actually created.
    // Only applies when inlineSourceSelected is null (user didn't pick a source via autocomplete
    // this session) — meaning the source value came from a stale DataGrid edit-model entry left
    // over from a previous failed new-source attempt.
    if (!newSource && !inlineSourceSelected && effectiveRow.source && effectiveRow.source !== oldRow.source) {
      const sourceExistsInData = sourceData.some((item) => item.source === effectiveRow.source);
      if (!sourceExistsInData) {
        const error = new Error('The source has not been created. Select an existing source or use the ADD option to create a new one with a channel category.');
        setAlertMessage({ severity: 'error', children: error.message });
        setSnackbarOpen(true);
        setErrorRows((prev) => (prev.some((row) => row.id === newRow.id) ? prev : [...prev, newRow]));
        throw error;
      }
    }

    const isNewSmsLongcodeSource = newSource && effectiveRow.channel_category === 'SMS_Longcode';

    let newSourceData;
    let sourceCreated = false;
    // Create the new source if all required fields are present.
    // For SMS_Longcode sources, send_date is required.
    if (newSource && effectiveRow.source && effectiveRow.channel_category
      && (!isNewSmsLongcodeSource || effectiveRow.send_date)) {
      // grouping source data
      newSourceData = {
        id: `${client}-${effectiveRow.source}`,
        source: effectiveRow.source,
        channel_category: effectiveRow.channel_category,
        client_id: client,
        from_goop: false,
        send_date: effectiveRow.send_date,
      };
      const sourceCheck = await checkSourceBeforeCreating(newSourceData.source);
      if (sourceCheck && sourceCheck[0] && sourceCheck[0].length === 0) {
        sourceCreated = await createSource(newSourceData);
      }
      if (sourceCreated) {
      // update code data with source and other fields
        const codesData = {
          id: effectiveRow.id,
          source: effectiveRow.source,
          client_id: client,
          from_goop: false,
          code: effectiveRow.code,
          channel_category: effectiveRow.channel_category,
          channel: effectiveRow.channel,
          revenue_category: effectiveRow.revenue_category,
          type: effectiveRow.type,
          audience: effectiveRow.audience,
          audience_category: effectiveRow.audience_category,
          fixed_cost_media_spend: effectiveRow.fixed_cost_media_spend,
          fixed_cost_cpa_override: effectiveRow.fixed_cost_cpa_override,
          send_cost: effectiveRow.send_cost,
          send_size: effectiveRow.send_size,
          send_date: effectiveRow.send_date,
        };
        // update code using appsync
        await updateCode(codesData);
        // Remove the row ID from the errorRows state if validation passes
        setErrorRows((prev) => prev.filter((row) => row.id !== effectiveRow.id));

        setPageState((prevState) => {
          const updatedData = prevState.data.map((row) => (row.id === effectiveRow.id ? effectiveRow : row));
          return {
            ...prevState,
            data: updatedData,
          };
        });
        // reset state
        setEditRowData({});
        setSourceSearch('');
        setNewSource(false);
      }
    } else if (newSource
&& (!effectiveRow.source || !effectiveRow.channel_category || (isNewSmsLongcodeSource && !effectiveRow.send_date))) {
      // trying to create source without channel_category, or an SMS_Longcode source without a send date
      const error = new Error(
        isNewSmsLongcodeSource
          ? 'You must fill out the source, channel category, and send date fields before creating a new SMS_Longcode source.'
          : 'You must fill out the source and channel category fields before creating a new source.',
      );
      setAlertMessage({
        severity: 'error',
        children: error.message,
      });
      setSnackbarOpen(true);
      setErrorRows((prev) => {
        const isAlreadyInErrorRows = prev.some((row) => row.id === effectiveRow.id);
        if (isAlreadyInErrorRows) return prev;
        return [...prev, effectiveRow];
      });
      // throw error so that user can't submit changes to the row until it's fixed
      throw error;
    } else {
      // let's update codes data
      const codesData = {
        id: effectiveRow.id,
        client_id: client,
        from_goop: false,
        code: effectiveRow.code,
        channel: effectiveRow.channel,
        channel_category: effectiveRow.channel_category,
        source: effectiveRow.source,
        version: effectiveRow.version,
        jira_ticket_num: effectiveRow.jira_ticket_num,
        revenue_category: effectiveRow.revenue_category,
        type: effectiveRow.type,
        audience: effectiveRow.audience,
        audience_category: effectiveRow.audience_category,
        fixed_cost_media_spend: effectiveRow.fixed_cost_media_spend,
        fixed_cost_cpa_override: effectiveRow.fixed_cost_cpa_override,
        send_cost: effectiveRow.send_cost,
        send_size: effectiveRow.send_size,
        send_date: effectiveRow.send_date,
      };
      // update code using appsync
      await updateCode(codesData);
      // Remove the row ID from the errorRows state if validation passes
      setErrorRows((prev) => prev.filter((row) => row.id !== effectiveRow.id));
      setPageState((prevState) => {
        const updatedData = prevState.data.map((row) => (row.id === effectiveRow.id ? effectiveRow : row));
        return { ...prevState, data: updatedData };
      });
      setEditRowData({});
      setNewSource(false);
      setSourceSearch('');
    }
    // return new updated row
    return effectiveRow;
  };

  const actionDefault = (
    <IconButton
      size="small"
      aria-label="close"
      color="inherit"
      onClick={handleClose}
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  );

  useEffect(() => {
    setOrderedColumns(columns);
  }, [sourceOptions, newSource]);

  useEffect(() => {
    const codesColumnsLocStore = getLocalStorageTablePref('codes', 'column');
    const isCodesColDefUpdated = !diffListOfObjects(columns, codesColumnsLocStore);

    if (!codesColumnsLocStore || isCodesColDefUpdated) {
      updateTablePreferenceLocalStorage('codes', 'column', columns);
    } else {
      let elToUpdate = codesColumnsLocStore[0];
      elToUpdate = {
        ...elToUpdate,
        getActions: (row) => [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon color={row.row.from_goop ? 'disabled' : 'inherit'} />}
            label="Edit"
            className="textPrimary"
            onClick={(ev) => handleEditClick(ev, row)}
            color="inherit"
            size="medium"
          />,
        ],
      };
      codesColumnsLocStore[0] = elToUpdate;
      setOrderedColumns(codesColumnsLocStore);
    }
  }, [pageState]);

  useEffect(() => {
    const codesColumnVisibility = getLocalStorageTablePref('codes', 'column-visibility');
    if (!codesColumnVisibility) {
      updateTablePreferenceLocalStorage('codes', 'column-visibility', initColumnVisibilityModel);
    } else {
      setColumnVisibilityModel(codesColumnVisibility);
    }

    // subscribe to row edit start and stop events to track which row is being edited and handle escape key behavior
    if (!gridApiRef.current || !gridApiRef.current.subscribeEvent) return undefined;
    const unsubStart = gridApiRef.current.subscribeEvent('rowEditStart', (params) => {
      editingRowOriginalRef.current = params.row;
      setEditRowData({});
      setNewSource(false);
      setSourceSearch('');
    });
    const unsubStop = gridApiRef.current.subscribeEvent('rowEditStop', (params) => {
      if (params.reason === 'escapeKeyDown') {
        if (editingRowOriginalRef.current) {
          gridApiRef.current.updateRows([{
            id: params.id,
            channel_category: editingRowOriginalRef.current.channel_category,
          }]);
        }
        // processRowUpdate is NOT called on escape, so clear state here
        setEditRowData({});
        setNewSource(false);
        setSourceSearch('');
      }
      // For all other reasons (rowFocusOut, enterKeyDown, etc.) processRowUpdate
      // fires after this event and still needs editRowData — it clears its own state
      editingRowOriginalRef.current = null;
    });
    return () => {
      unsubStart();
      unsubStop();
    };
  }, []);

  useEffect(() => {
    getSourcesForDropdown();
  }, [client, authenticated, sourceSearch]);

  // used for chips
  const [userCriteria, setUserCriteria] = useState([]);

  const defaultSearch = [
    {
      col: 'source',
      op: 'exists',
      criteria: 'false',
      children: [],
      id: 1,
    },
    {
      col: 'most_recent_transaction_date',
      op: 'gte',
      criteria: thirtyDaysAgoString,
      children: [],
      id: 3,
    },
  ];

  // search criteria user has entered in the advanced search
  const [currUserSearch, setCurrUserSearch] = useState(defaultSearch);

  const deleteCodesChip = (parentIndex) => {
    setCurrUserSearch((prevState) => {
      const newState = [...prevState];
      newState.splice(parentIndex, 1);
      return newState;
    });
  };

  const defaultSort = {
    field: 'createdAt',
    direction: 'desc',
  };

  const [advancedSearchSort, setAdvancedSearchSort] = useState(defaultSort);

  const getDefaultColumns = () => codeColumns;
  const isRowSelectable = (params) => !params.row.from_goop;
  const isCellEditable = (params) => !params.row.from_goop;

  return (
    <>
      <div className="table-actions">
        <div className="batch-edit-btn-container">
          <Button
            variant="contained"
            onClick={handleBulkEditClick}
          >
            Bulk Edit
          </Button>
          <p>
            {selectedRows.length}
            {' '}
            selected
          </p>
        </div>
        <AdvancedSearchV2
          currUserSearch={currUserSearch}
          setCurrUserSearch={setCurrUserSearch}
          searchSetter={setAdvancedSearchQuery}
          sortSetter={setAdvancedSearchSort}
          lockedCriteria={lockedCriteria}
          defaultCriteria={defaultSearch}
          defaultSort={defaultSort}
          columns={columns}
          lockedColumns={['client_id']}
          setUserChips={setUserCriteria}
        />
      </div>
      <AdvancedSearchChip
        columns={columns}
        lockedCriteria={lockedCriteria}
        userCriteria={userCriteria}
        setUserCriteria={setUserCriteria}
        onDelete={deleteCodesChip}
      />
      {snackbarOpen && alertMessage
              && (
                <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => { setSnackbarOpen(false); setAlertMessage({}); }}>
                  <Alert
                    severity={alertMessage.severity}
                    onClose={() => { setSnackbarOpen(false); setAlertMessage({}); }}
                  >
                    {alertMessage.children}
                  </Alert>
                </Snackbar>
              )}
      <div style={{ flexGrow: 1 }}>
        {client && (
        <ServerTable
          pageState={pageState}
          updatePageState={setPageState}
          columns={orderedColumns}
          columnVisibilityModel={columnVisibilityModel}
          setColumnVisibilityModel={setColumnVisibilityModel}
          filters={advancedSearchQuery}
          sortModel={advancedSearchSort}
          queryName="searchCodes"
          tablePrefix="codes-adv"
          checkboxSelection
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          createRows={createCodeRows}
          getDefaultColumns={getDefaultColumns}
          setOrderedColumns={() => { }} // ls/saving prefs is in flux, adding this to prevent errs
          isRowSelectable={isRowSelectable}
          isCellEditable={isCellEditable}
          apiRef={gridApiRef}
          processRowUpdate={handleInlineEdit}
          onProcessRowUpdateError={handleInlineEditError}
          getRowClassName={(params) => {
            const classes = [];
            errorRows.forEach((row) => {
              if (row.id === params.id) {
                // used to highlight a row with an error
                classes.push('error-row');
              }
            });
            // alternate rows with gray / white background color
            if (params.indexRelativeToCurrentPage % 2 === 0) {
              classes.push('even');
            } else {
              classes.push('odd');
            }
            return classes.join(' ');
          }}
          tabNavigation="content"
        />
        )}
      </div>

      <Dialog onClose={handleCloseDialog} open={isDialogOpen} maxWidth="sm" fullWidth>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', m: 1 }}>
          <DialogTitle>Editing Code</DialogTitle>
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={handleCloseDialog}
            sx={{ pr: 2 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignContent: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            flexGrow: 1,
            p: 3,
            m: 1,
            borderRadius: 1,
          }}
        >
          <ClientTextField
            setFunction={setter}
            data={editRowData.client_id}
            attribute="client_id"
            label="Client"
            isDisabled
          />
          <ClientTextField
            setFunction={setter}
            data={editRowData.code}
            attribute="code"
            label="Code"
            isDisabled
          />
          <div className="codes-source-search-container">
            <MPAutocomplete
              setFunction={setter}
              dropdownOptions={sourceOptions}
              label={(
                <>
                  Source –
                  <strong> Channel Category</strong>
                </>
              )}
              editRowData={editRowData}
              field="source"
              setSourceSearch={setSourceSearch}
            />
            {searching && <Loading />}
          </div>
          <ClientDropDown
            key={`channel-category-${editRowData.channel_category || ''}`}
            setFunction={setter}
            data={editRowData.channel_category}
            attribute="channel_category"
            label="Channel Category"
            isDisabled={!!editRowData.source}
            structure={structure}
          />
          {(editRowData.channel_category === 'SMS_Longcode' || editRowData.channel_category === 'SMS_Shortcode') ? (
            <ClientDropDown
              key={`channel-${editRowData.channel || ''}`}
              setFunction={setter}
              data={editRowData.channel}
              attribute="channel"
              label="Channel"
              structure={structure}
            />
          ) : (
            <ClientTextField
              setFunction={setter}
              data={editRowData.channel}
              attribute="channel"
              label="Channel"
            />
          )}
          {editRowData.channel_category === 'SMS_Longcode' && (
            editRowData.source ? (
              <ClientTextField
                setFunction={() => { }}
                data={editRowData.send_date}
                attribute="send_date"
                label="Send Date"
                isDisabled
              />
            ) : (
              <>
                <InputLabel variant="standard" htmlFor="send_date">
                  Send Date
                </InputLabel>
                <CustomDatePicker
                  format="MM/DD/YYYY"
                  setCriteria={setter}
                  criteria={editRowData.send_date || ''}
                  name="send_date"
                />
              </>
            )
          )}
          {editRowData.channel_category === 'SMS_Longcode' && editRowData.channel === 'MissionSMS' && (
            <ClientTextField
              setFunction={setter}
              data={(editRowData && editRowData.jira_ticket_num) ? editRowData.jira_ticket_num : ''}
              attribute="jira_ticket_num"
              label="Jira Ticket Number"
            />
          )}
          {editRowData.channel_category === 'SMS_Longcode' && (
            <>
              <ClientTextField
                setFunction={setter}
                data={(editRowData && editRowData.version) ? editRowData.version : ''}
                attribute="version"
                label="Version"
              />
              <ClientTextField
                setFunction={setter}
                data={(editRowData && editRowData.send_cost) ? editRowData.send_cost : ''}
                attribute="send_cost"
                label="Send Cost"
                type="number"
              />
              <ClientTextField
                setFunction={setter}
                data={(editRowData && editRowData.send_size) ? editRowData.send_size : ''}
                attribute="send_size"
                label="Send Size"
                type="number"
              />
            </>

          )}
          <ClientTextField
            setFunction={setter}
            data={editRowData.audience}
            attribute="audience"
            label="Audience"
          />
          <ClientTextField
            setFunction={setter}
            data={editRowData.audience_category}
            attribute="audience_category"
            label="Audience Category"
          />
          <ClientTextField
            setFunction={setter}
            data={editRowData.revenue_category}
            attribute="revenue_category"
            label="Revenue Category"
          />
          {editRowData.channel_category === 'SMS_Longcode' ? (
            <p>Revenue Type will be automatically set to p2p for SMS_Longcode codes.</p>
          ) : (
            <ClientDropDown
              setFunction={setter}
              data={editRowData && editRowData.type ? editRowData.type : ''}
              attribute="type"
              label="Revenue Type"
              structure={structure}
            />
          )}
          {editRowData.channel_category !== 'SMS_Longcode' && (
            <>
              <ClientTextField
                setFunction={setter}
                data={editRowData.fixed_cost_cpa_override}
                attribute="fixed_cost_cpa_override"
                label="Fixed Cost CPA"
                type="number"
              />
              <ClientTextField
                setFunction={setter}
                data={editRowData.fixed_cost_media_spend}
                attribute="fixed_cost_media_spend"
                label="Fixed Cost Media Spend"
                type="number"
              />
              <ClientTextField
                setFunction={setter}
                data={editRowData.most_recent_spend}
                attribute="most_recent_spend"
                label="Most Recent Spend"
                type="number"
              />
            </>
          )}
          <Button
            className="datagrid-rightside-buttons"
            variant="contained"
            style={{
              justifyContent: 'center',
              marginTop: '20px',
              marginBottom: '15px',
            }}
            onClick={handleModalSave}
          >
            Save
          </Button>
        </Box>
      </Dialog>

      {selectedRows
        && (
          <Dialog onClose={handleBulkEditClose} open={bulkEditOpen} maxWidth="sm" fullWidth>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', m: 1 }}>
              <DialogTitle>
                Bulk Editing
                {' '}
                {selectedRows.length}
                {' '}
                Codes
              </DialogTitle>
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleBulkEditClose}
                sx={{ pr: 2 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignContent: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                flexGrow: 1,
                p: 3,
                m: 1,
                borderRadius: 1,
              }}
            >
              <div className="bulk-edit-codes-list">
                <p>Codes:</p>
                <ul>
                  {selectedRows.map((row) => <li key={row.code}>{row.code}</li>)}
                </ul>
              </div>
              <div className="codes-source-search-container">
                <MPAutocomplete
                  setFunction={bulkEditsetter}
                  dropdownOptions={sourceOptions}
                  editRowData={(bulkEditFormData && bulkEditFormData.source) ? bulkEditFormData : {}}
                  label={(
                    <>
                      Source –
                      <strong> Channel Category</strong>
                    </>
                  )}
                  field="source"
                  setSourceSearch={setSourceSearch}
                />
                {searching && <Loading />}
              </div>
              <ClientDropDown
                key={`channel-category-${bulkEditFormData.channel_category || ''}`}
                setFunction={bulkEditsetter}
                data={bulkEditFormData.channel_category}
                attribute="channel_category"
                label="Channel Category"
                isDisabled={!!bulkEditFormData.source}
                structure={structure}
              />
              {(bulkEditFormData.channel_category === 'SMS_Longcode' || bulkEditFormData.channel_category === 'SMS_Shortcode') ? (
                <ClientDropDown
                  setFunction={bulkEditsetter}
                  data={(bulkEditFormData && bulkEditFormData.channel) ? bulkEditFormData.channel : ''}
                  attribute="channel"
                  label="Channel"
                  structure={structure}
                />
              ) : (
                <ClientTextField
                  setFunction={bulkEditsetter}
                  data={bulkEditFormData.channel}
                  attribute="channel"
                  label="Channel"
                />
              )}
              {bulkEditFormData.channel_category === 'SMS_Longcode' && bulkEditFormData.channel === 'MissionSMS' && (
                <ClientTextField
                  setFunction={bulkEditsetter}
                  data={(bulkEditFormData && bulkEditFormData.jira_ticket_num) ? bulkEditFormData.jira_ticket_num : ''}
                  attribute="jira_ticket_num"
                  label="Jira Ticket Number"
                />
              )}
              {bulkEditFormData.channel_category === 'SMS_Longcode' && (
                <>
                  <ClientTextField
                    setFunction={bulkEditsetter}
                    data={bulkEditFormData.version}
                    attribute="version"
                    label="Version"
                  />
                  {bulkEditFormData.source ? (
                    <ClientTextField
                      setFunction={() => { }}
                      data={bulkEditFormData.send_date}
                      attribute="send_date"
                      label="Send Date"
                      isDisabled
                    />
                  ) : (
                    <>
                      <InputLabel variant="standard" htmlFor="send_date">
                        Send Date
                      </InputLabel>
                      <CustomDatePicker
                        format="MM/DD/YYYY"
                        setCriteria={bulkEditsetter}
                        criteria={bulkEditFormData.send_date || ''}
                        name="send_date"
                      />
                    </>
                  )}
                  <ClientTextField
                    setFunction={bulkEditsetter}
                    data={(bulkEditFormData && bulkEditFormData.send_cost) ? bulkEditFormData.send_cost : ''}
                    attribute="send_cost"
                    label="Send Cost"
                    type="number"
                  />
                  <ClientTextField
                    setFunction={bulkEditsetter}
                    data={(bulkEditFormData && bulkEditFormData.send_size) ? bulkEditFormData.send_size : ''}
                    attribute="send_size"
                    label="Send Size"
                    type="number"
                  />
                </>

              )}
              <ClientTextField
                setFunction={bulkEditsetter}
                data={(bulkEditFormData && bulkEditFormData.audience) ? bulkEditFormData.audience : ''}
                attribute="audience"
                label="Audience"
              />
              <ClientTextField
                setFunction={bulkEditsetter}
                data={(bulkEditFormData && bulkEditFormData.audience_category) ? bulkEditFormData.audience_category : ''}
                attribute="audience_category"
                label="Audience Category"
              />
              <ClientTextField
                setFunction={bulkEditsetter}
                data={(bulkEditFormData && bulkEditFormData.revenue_category) ? bulkEditFormData.revenue_category : ''}
                attribute="revenue_category"
                label="Revenue Category"
              />
              {bulkEditFormData.channel_category === 'SMS_Longcode' ? (
                <p>Revenue Type will be automatically set to p2p for SMS_Longcode codes.</p>
              ) : (
                <ClientDropDown
                  setFunction={bulkEditsetter}
                  data={(bulkEditFormData && bulkEditFormData.type) ? bulkEditFormData.type : ''}
                  attribute="type"
                  label="Revenue Type"
                  structure={structure}
                />
              )}
              <Button
                className="datagrid-rightside-buttons"
                variant="contained"
                style={{
                  marginTop: '20px',
                  marginBottom: '15px',
                }}
                onClick={togglePreview}
              >
                Preview Changes
              </Button>
              <Button
                className="datagrid-rightside-buttons"
                variant="contained"
                style={{
                  marginTop: '20px',
                  marginBottom: '15px',
                }}
                onClick={handleBulkEditFormCancel}
              >
                Cancel
              </Button>
            </Box>
          </Dialog>
        )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={pageState.isPreview ? null : 6000}
        onClose={handleClose}
        message={snackMessage}
        action={snackWithConfirm ? actionForBulkEditUpdate : actionDefault}
      />
    </>
  );
}

export default CodeGroupings;

CodeGroupings.propTypes = {
  client: PropTypes.string,
};
